// ---------------------------------------------------------------------------
// DOM utilities for the page editor canvas.
//
// resolveVisibleElement() solves the `display: contents` problem:
// element-renderer wraps elements in <div className="contents" data-element-id="...">.
// `display: contents` elements have no CSS box — getBoundingClientRect()
// returns {0,0,0,0} and CSS outline doesn't render. This utility finds
// the first visible descendant to use for visual operations (highlighting,
// positioning overlays, measuring bounding rects).
// ---------------------------------------------------------------------------

/**
 * Given a DOM element that may have `display: contents`, return the first
 * descendant element that actually generates a CSS box (i.e. is visible and
 * has a measurable bounding rect).
 *
 * If the element itself is visible, returns it unchanged.
 * Recurses through nested `display: contents` elements.
 */
export function resolveVisibleElement(el: HTMLElement): HTMLElement {
  const style = getComputedStyle(el)
  if (style.display !== 'contents') return el

  // Walk element children (skip text nodes)
  for (let i = 0; i < el.children.length; i++) {
    const child = el.children[i] as HTMLElement
    if (!child || child.nodeType !== Node.ELEMENT_NODE) continue
    const resolved = resolveVisibleElement(child)
    if (resolved) return resolved
  }

  // Fallback: return original element if no visible child found
  return el
}
